/**
 * UPU S10 tracking-number client-side validator.
 *
 * !! REGEX DUPLICATED FROM:
 *    apps/service-mail/src/modules/tracking-number/tracking-number.ts
 *      (TRACKING_NUMBER_REGEX + COUNTRY_CODE + SERVICE_CLASS_PREFIX)
 *
 * The citizen app validates structurally (and against the mod-11 check digit)
 * before calling the API so obviously malformed input doesn't burn a network
 * round-trip. The server is still the source of truth — it re-validates and
 * resolves to a row.
 *
 * Spec reference: UPU S10 §2.6, mirrored in F-016 FR-MAIL-015._3.
 *
 * Layout (13 chars total when stored, no separators):
 *   LL DDDDDDDD C NG
 *   - LL  : 2-letter UPMP service indicator (RR / EE / CP / RA).
 *   - 8 D : zero-padded serial.
 *   - C   : mod-11 check digit (single digit).
 *   - NG  : ISO 3166-1 alpha-2 for Nigeria (fixed for items originated locally).
 */

/** Strict structural matcher — copy of TRACKING_NUMBER_REGEX. */
export const UPU_S10_REGEX = /^[A-Z]{2}[0-9]{8}[0-9][A-Z]{2}$/;

/** Service-indicator whitelist — copy of SERVICE_CLASS_PREFIX values. */
const KNOWN_PREFIXES: ReadonlySet<string> = new Set(['RR', 'EE', 'CP', 'RA']);

/** Country code suffix — copy of COUNTRY_CODE. */
const COUNTRY_CODE = 'NG';

/** UPU S10 weights applied left-to-right against the 8-digit serial. */
const S10_WEIGHTS: readonly number[] = [8, 6, 4, 2, 3, 5, 9, 7];

function s10Checksum(digits8: string): number {
  let sum = 0;
  for (let i = 0; i < 8; i += 1) {
    const d = digits8.charCodeAt(i) - 48; // '0' is 48
    const w = S10_WEIGHTS[i] ?? 0;
    sum += d * w;
  }
  const remainder = sum % 11;
  const candidate = 11 - remainder;
  if (candidate === 10) return 0;
  if (candidate === 11) return 5;
  return candidate;
}

/**
 * Returns true iff the input is a structurally valid UPU S10 number with a
 * matching mod-11 check digit AND a known UPMP service indicator AND the
 * NG country code suffix.
 *
 * Mirrors apps/service-mail/.../tracking-number.ts `validateTrackingNumber`
 * exactly so the client can short-circuit lookups the server would reject.
 */
export function isValidTrackingNumber(input: string): boolean {
  if (typeof input !== 'string') return false;
  if (!UPU_S10_REGEX.test(input)) return false;
  if (!input.endsWith(COUNTRY_CODE)) return false;
  const prefix = input.slice(0, 2);
  if (!KNOWN_PREFIXES.has(prefix)) return false;
  const serial = input.slice(2, 10);
  const expected = s10Checksum(serial);
  const actual = Number(input[10]);
  return expected === actual;
}

/** Display normaliser — uppercase + trim. The form runs this on every
 *  keystroke so users can paste lowercase numbers without the field
 *  rejecting them. */
export function normaliseTrackingNumber(input: string): string {
  return input.trim().toUpperCase();
}
